import posthog from 'posthog-js'

/**
 * Product analytics (PostHog). Single entry point for the whole frontend.
 *
 * Rules:
 * - Nothing here may ever throw or block the UI: every call is wrapped and is a
 *   no-op when PostHog is not configured (REACT_APP_POSTHOG_KEY missing) or blocked
 *   by the browser (ad blockers, privacy mode).
 * - distinct_id is the backend user id, the same one the backend uses for its own
 *   events, so frontend and backend events land on the same person.
 *
 * Event naming convention (so funnels and flows stay consistent):
 * - snake_case, `object_action` in past tense: `item_created`, `listing_published`,
 *   `csv_import_started`. Never start with `$` (reserved by PostHog).
 * - Properties: snake_case, stable keys across events, no secrets or free text typed
 *   by the user.
 *
 * Tracking a button without touching JS: add `data-track-event="item_create_clicked"`
 * (and optionally `data-track-props='{"from":"toolbar"}'`) to any element.
 */

const TRACK_EVENT_ATTRIBUTE = 'data-track-event'
const TRACK_PROPS_ATTRIBUTE = 'data-track-props'

let enabled = false
let identifiedUserId = null
let clickTrackingInstalled = false

function safely(action) {
  try {
    return action()
  } catch {
    return undefined
  }
}

export function initAnalytics() {
  if (enabled) return

  const key = process.env.REACT_APP_POSTHOG_KEY
  if (!key) return

  safely(() => {
    posthog.init(key, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
      // Anonymous until identify(): cheaper, and the anonymous visit (e.g. /login)
      // is merged into the person once they log in.
      person_profiles: 'identified_only',
      // Pageviews are sent by AnalyticsTracker on every client-side route change.
      capture_pageview: false,
      capture_pageleave: true,
      // Explicit events only; no automatic click/DOM capture.
      autocapture: false,
      disable_session_recording: true,
    })
    enabled = true
  })

  installClickTracking()
}

export function isAnalyticsEnabled() {
  return enabled
}

export function trackEvent(name, properties) {
  if (!enabled || !name) return
  safely(() => posthog.capture(name, properties))
}

export function trackPageview(properties) {
  trackEvent('$pageview', properties)
}

/**
 * Links the browser to the backend user. Safe to call repeatedly: the same user is
 * only identified once per session.
 */
export function identifyUser({ id, username, email, role } = {}) {
  if (!enabled || !id) return
  const userId = String(id)
  if (identifiedUserId === userId) return

  safely(() => {
    const personProperties = {}
    if (username) personProperties.username = username
    if (email) personProperties.email = email
    if (role) personProperties.role = role
    posthog.identify(userId, personProperties)
    identifiedUserId = userId
  })
}

/** Call on logout so the next person on this browser is not merged into the previous one. */
export function resetAnalytics() {
  if (!enabled || identifiedUserId === null) return
  safely(() => posthog.reset())
  identifiedUserId = null
}

function parseTrackProps(element) {
  const raw = element.getAttribute(TRACK_PROPS_ATTRIBUTE)
  if (!raw) return undefined
  const parsed = safely(() => JSON.parse(raw))
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : undefined
}

function installClickTracking() {
  if (clickTrackingInstalled || typeof document === 'undefined') return
  clickTrackingInstalled = true

  document.addEventListener(
    'click',
    (event) => {
      safely(() => {
        const element = event.target?.closest?.(`[${TRACK_EVENT_ATTRIBUTE}]`)
        const name = element?.getAttribute(TRACK_EVENT_ATTRIBUTE)
        if (!name) return
        trackEvent(name, { path: window.location.pathname, ...parseTrackProps(element) })
      })
    },
    true,
  )
}
